import { type Fiche, type Language, type Patch, PatchResponse } from '@pharmafiche/schema';
import { AiClientError } from './client';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_request: 'Requête invalide.',
  rate_limited: 'Limite atteinte. Patiente quelques minutes.',
  ai_invalid_response: "Le modèle n'a pas renvoyé de patch valide. Reformule ou réessaie.",
  not_implemented: 'Pas encore disponible.',
  config_missing: 'Service IA non configuré.',
  internal_error: 'Échec serveur. Réessaie dans quelques instants.',
};

export interface PatchParams {
  fiche: Fiche;
  prompt: string;
  language: Language;
  target_section_id?: string;
  target_bullet_id?: string;
}

export interface PatchOutcome {
  patch: Patch;
  model_used: string;
  latency_ms: number;
}

export async function requestPatch(params: PatchParams): Promise<PatchOutcome> {
  let res: Response;
  try {
    res = await fetch('/api/patch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fiche: params.fiche,
        prompt: params.prompt,
        language: params.language,
        ...(params.target_section_id ? { target_section_id: params.target_section_id } : {}),
        ...(params.target_bullet_id ? { target_bullet_id: params.target_bullet_id } : {}),
      }),
    });
  } catch (err) {
    throw new AiClientError(
      'network',
      'Connexion impossible. Vérifie ta connexion internet.',
      0,
      err instanceof Error ? err.message : undefined,
    );
  }

  if (!res.ok) {
    let code = 'internal_error';
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: { code?: string; details?: unknown } };
      if (data.error?.code) code = data.error.code;
      details = data.error?.details;
    } catch {
      // body wasn't JSON
    }
    const friendly = ERROR_MESSAGES[code] ?? `Erreur ${res.status}`;
    throw new AiClientError(code, friendly, res.status, details);
  }

  const data = (await res.json()) as unknown;
  const parsed = PatchResponse.safeParse(data);
  if (!parsed.success) {
    throw new AiClientError(
      'client_parse',
      "La réponse du serveur n'est pas reconnue. Réessaie.",
      200,
      parsed.error.issues.slice(0, 3),
    );
  }

  return {
    patch: parsed.data.data,
    model_used: parsed.data.model_used,
    latency_ms: parsed.data.latency_ms,
  };
}
